/*
 * hs-np.js — Nova Poshta delivery picker for the HolyShape cart (cart page and
 * cart drawer). Replaces the CopyCon vendor widget (assets/nova-poshta.js).
 *
 * What changed against the vendor widget, and why:
 *  - Lookups go through our server (/api/np/*). The vendor widget called Nova
 *    Poshta from the browser with a cabinet key printed into every page.
 *  - The cart is never rebuilt. The vendor widget sent shoppers to a cart
 *    permalink (/cart/<variant>:<qty>?…), which drops line-item properties
 *    (the pre-order marks). We save the choice into cart attributes and open
 *    /checkout?checkout[shipping_address][…], which keeps the cart as it is.
 *  - The attribute names and value formats are exactly the vendor widget's
 *    (_delivery_type, _delivery_warehouse_Ref, «Відділення:25», «, буд.5» …):
 *    the shipping app and the fulfilment warehouse read them from the order.
 *  - Lists open inside the page flow, not over it, so the fixed checkout bar
 *    on phones cannot cover them; every field is keyboard- and screen-reader
 *    operable; a failed lookup never blocks checkout.
 *
 * Config (snippets/hs-np.liquid):
 *   window.HSNP = { api: 'https://…/api/np', mode: 'live' | 'preview' }
 *   preview: inactive unless this browser opted in with ?hs_np_preview=1
 *   (?hs_np_preview=0 opts out); the vendor widget keeps running for everyone else.
 */
(function (factory) {
  var lib = factory();
  if (typeof module === 'object' && module.exports) module.exports = lib;
  else if (typeof window !== 'undefined') lib.boot(window, document);
})(function () {
  'use strict';

  var VERSION = 'hs-np 2';

  // The cart's button said «Оформити замовлення», the same words as the real
  // last button in Shopify's checkout, and many clients who confirmed their
  // number in Telegram took the cart step for the finish (owner, 13.10.2026).
  // The theme's text stays; this script, which already owns the cart, says
  // what the button really does. Anything after the words (a price) is kept.
  var CHECKOUT_LABEL = 'Далі: доставка й оплата';
  var CHECKOUT_WORDS = /оформити\s+замовлення/i;
  function relabel(text) {
    var s = String(text == null ? '' : text);
    return CHECKOUT_WORDS.test(s) ? s.replace(CHECKOUT_WORDS, CHECKOUT_LABEL) : s;
  }
  var PROVIDER = 'Нова пошта';
  var COUNTRY = 'Ukraine';
  var METHOD = { branch: 'Відділення', courier: 'Курʹєр' };   // U+02B9, as the vendor widget wrote it
  var ZIP = '00000';

  // Every attribute the vendor widget ever wrote. Unused ones are sent as ''
  // (Shopify drops an empty attribute), so a switch of type leaves no leftovers.
  var ATTR_KEYS = [
    '_provider', '_country', '_delivery_type', '_delivery_method',
    '_delivery_city', '_delivery_city_name', '_delivery_city_Area', '_delivery_city_Ref',
    '_delivery_warehouse', '_delivery_warehouse_zip', '_delivery_warehouse_name', '_delivery_warehouse_address',
    '_delivery_warehouse_CityRef', '_delivery_warehouse_Number', '_delivery_warehouse_Ref',
    '_delivery_warehouse_SettlementRef', '_delivery_warehouse_TypeOfWarehouse',
    '_delivery_courier_address', '_delivery_courier_street', '_delivery_courier_house', '_delivery_courier_flat',
    '_delivery_street_Ref',
    // Hers, not Nova Poshta's: the name and number the parcel is made out to.
    // Staff read them on the order the same way they read the branch.
    '_recipient_name', '_recipient_phone',
    // Which half of «доставка — за наш рахунок» this cart was in, and the cart
    // it was (services/cart-delivery.js). Nothing is charged for either.
    '_np_price', '_np_price_cart',
  ];

  function clean(s) { return String(s == null ? '' : s).replace(/\s+/g, ' ').trim(); }

  function emptyState() {
    return { type: 'branch', city: null, warehouse: null, street: null, house: '', flat: '', name: '', phone: '' };
  }

  // «Дарія Ткаченко» → { first: 'Дарія', last: 'Ткаченко' }. Shopify wants the
  // two separately; she should not have to think about that. A single word is
  // kept as the first name — a half-filled checkout still beats an empty one.
  function splitName(full) {
    var parts = clean(full).split(' ').filter(Boolean);
    if (!parts.length) return { first: '', last: '' };
    if (parts.length === 1) return { first: parts[0], last: '' };
    return { first: parts.slice(0, -1).join(' '), last: parts[parts.length - 1] };
  }

  // The same rule the server uses (services/ugc-apply.js): +380XXXXXXXXX and
  // 0XXXXXXXXX in every spacing people actually type. Anything else is not
  // passed on — Shopify would only answer with an error of its own.
  function normalizePhoneUA(raw) {
    var d = String(raw == null ? '' : raw).replace(/[\s()\-.]/g, '');
    var m = /^(?:\+?380(\d{9})|0(\d{9}))$/.exec(d);
    return m ? '+380' + (m[1] || m[2]) : null;
  }

  // ─── Pure logic (tested in test/hs-np.test.js) ─────────────────────────────

  function buildAttributes(state) {
    var a = {};
    ATTR_KEYS.forEach(function (k) { a[k] = ''; });
    var type = state && state.type;
    if (type !== 'branch' && type !== 'courier') return a;   // international: nothing of ours on the order

    a._provider = PROVIDER;
    a._country = COUNTRY;
    a._delivery_type = type;
    a._delivery_method = METHOD[type];
    if (state.city) {
      a._delivery_city = clean(state.city.label || state.city.name);
      a._delivery_city_name = clean(state.city.name);
      a._delivery_city_Ref = clean(state.city.ref);
    }
    a._recipient_name = clean(state.name);
    a._recipient_phone = normalizePhoneUA(state.phone) || clean(state.phone);
    if (state.priceArm) { a._np_price = clean(state.priceArm); a._np_price_cart = clean(state.priceCart); }
    if (type === 'branch' && state.warehouse) {
      var w = state.warehouse;
      a._delivery_warehouse = clean(w.description);
      a._delivery_warehouse_zip = ZIP;
      a._delivery_warehouse_name = clean(w.label);
      a._delivery_warehouse_address = clean(w.shortAddress);
      a._delivery_warehouse_CityRef = clean(w.cityRef);
      a._delivery_warehouse_Number = clean(w.number);
      a._delivery_warehouse_Ref = clean(w.ref);
      a._delivery_warehouse_SettlementRef = clean(w.settlementRef);
      a._delivery_warehouse_TypeOfWarehouse = clean(w.typeRef);
    }
    if (type === 'courier' && state.street) {
      var street = clean(state.street.name);
      var house = clean(state.house) ? ', буд.' + clean(state.house) : '';
      var flat = clean(state.flat) ? ', кв.' + clean(state.flat) : '';
      a._delivery_courier_street = street;
      a._delivery_courier_house = house;
      a._delivery_courier_flat = flat;
      a._delivery_courier_address = street + house + flat;
      a._delivery_street_Ref = clean(state.street.ref);
    }
    return a;
  }

  // Back from the cart attributes to a state, so a reload (or the drawer
  // opening on another page) shows what was already chosen.
  function stateFromAttributes(attrs) {
    var s = emptyState();
    attrs = attrs || {};
    if (attrs._delivery_type === 'courier') s.type = 'courier';
    else if (attrs._delivery_type === 'branch') s.type = 'branch';
    else if (attrs._delivery_type === 'other') s.type = 'other';
    if (attrs._delivery_city_Ref) {
      s.city = { ref: attrs._delivery_city_Ref, name: attrs._delivery_city_name || '', label: attrs._delivery_city || attrs._delivery_city_name || '' };
    }
    if (s.type === 'branch' && attrs._delivery_warehouse_Ref) {
      s.warehouse = {
        ref: attrs._delivery_warehouse_Ref,
        number: attrs._delivery_warehouse_Number || '',
        description: attrs._delivery_warehouse || '',
        shortAddress: attrs._delivery_warehouse_address || '',
        cityRef: attrs._delivery_warehouse_CityRef || '',
        settlementRef: attrs._delivery_warehouse_SettlementRef || '',
        typeRef: attrs._delivery_warehouse_TypeOfWarehouse || '',
        label: attrs._delivery_warehouse_name || '',
        category: /^Поштомат/.test(attrs._delivery_warehouse_name || '') ? 'Postomat' : 'Branch',
      };
    }
    s.name = attrs._recipient_name || '';
    s.phone = attrs._recipient_phone || '';
    if (s.type === 'courier' && attrs._delivery_street_Ref) {
      s.street = { ref: attrs._delivery_street_Ref, name: attrs._delivery_courier_street || '' };
      s.house = String(attrs._delivery_courier_house || '').replace(/^,\s*буд\.\s*/, '');
      s.flat = String(attrs._delivery_courier_flat || '').replace(/^,\s*кв\.\s*/, '');
    }
    return s;
  }

  // Nova Poshta's fee for handing over money at the branch: 20 ₴ + 2% of the
  // order, paid by the buyer (owner confirmed against the cabinet, 21.09.2026).
  // Online payment has none — which is the reason to say the number out loud.
  var COD_FEE = { flat: 20, pct: 0.02 };
  function codFee(totalUah) {
    var total = Number(totalUah);
    if (!isFinite(total) || total <= 0) return null;
    return Math.round(COD_FEE.flat + total * COD_FEE.pct);
  }

  // What is still missing before checkout: null when nothing is.
  // On page load the widget writes the cart only for a cart that has items but
  // no delivery type yet (a fresh cart, like the vendor widget did). An empty
  // cart or one that already carries the choice needs no write.
  function needsInitialWrite(cart) {
    if (!cart || !(cart.item_count > 0)) return false;
    var attrs = cart.attributes || {};
    return !attrs._delivery_type;
  }

  // A node the shopper can actually see. The theme renders the cart drawer
  // twice and hides one copy with `visibility: hidden` — which keeps its
  // offsetParent and its client rects, so measuring only those picked the
  // hidden copy, and «Обери відділення зі списку» was written where nobody
  // could read it: the checkout button simply looked dead (critique 2026-09-21).
  function isShown(node, getStyle) {
    if (!node) return false;
    if (typeof node.checkVisibility === 'function') {
      try { return !!node.checkVisibility({ visibilityProperty: true, contentVisibilityAuto: true }); } catch (e) { /* older signature: fall through */ }
    }
    var boxed = (node.offsetParent != null)
      || (typeof node.getClientRects === 'function' && node.getClientRects().length > 0);
    if (!boxed) return false;
    if (typeof getStyle !== 'function') return true;
    // `visibility` is inherited, so the hidden copy only shows up from above.
    for (var n = node; n && n.nodeType === 1; n = n.parentElement) {
      var cs = getStyle(n) || {};
      if (cs.visibility === 'hidden' || cs.visibility === 'collapse' || cs.display === 'none') return false;
    }
    return true;
  }

  // Is this childList record the theme's cart markup arriving or leaving?
  // Text nodes never matter, and neither do the nodes our own scripts draw
  // (hs-np, hs-preorder, hs-me, hs-tg, the size block). Named one by one: the
  // own theme uses hs-* classes for its own markup too (`hs-drawer--large`),
  // so «any hs- class» hid the drawer redraw and the picker never mounted.
  var OWN_NODES = '.hs-np, .hs-money, .hs-sets, .hs-line-gone, .hs-po-cart-banner, .hs-po-split, .hs-po-legend, .hs-dlv, '
    + '.hs-me, .hs-me-cart, .hs-vip-lock, .hs-preorder-notice, .hs-war-notice, .hs-loyalty-notice, .hs-tg__bd, '
    + '.hs-size-ask, .hs-cart-undo, style, script, link';
  function ownNode(node) {
    if (!node || node.nodeType !== 1) return false;
    if (node.hasAttribute && (node.hasAttribute('data-hs-buybar') || node.hasAttribute('data-hs-lab'))) return true;
    try { return !!(node.matches && node.matches(OWN_NODES)); } catch (e) { return false; }
  }
  function cartMutation(record, selector) {
    var lists = [record.addedNodes || [], record.removedNodes || []];
    var t = record.target;
    var cart = t && t.closest ? t.closest(selector) : null;
    // Inside the cart, changes within our own picker / money card (its lists
    // redraw on every keystroke) are ours too. Stop at the cart container: the
    // body itself carries hs-* state classes.
    for (var a = t; cart && a && a !== cart; a = a.parentElement) {
      if (ownNode(a)) return false;
    }
    var inCart = !!cart;
    for (var l = 0; l < lists.length; l++) {
      for (var i = 0; i < lists[l].length; i++) {
        var n = lists[l][i];
        if (!n || n.nodeType !== 1 || ownNode(n)) continue;
        if (inCart) return true;
        if (n.matches && n.matches(selector)) return true;
        if (n.querySelector && n.querySelector(selector)) return true;
      }
    }
    return false;
  }

  // Name (first + last) and phone are asked here, in the cart, and carried
  // into the Telegram confirmation and the checkout. Shopify needs both parts
  // of the name and a phone once it is handed an address; without them it
  // opened in red («Введіть ім'я / прізвище / номер телефону», 2026-10-04).
  // International orders get no address from us, so nothing is asked there.
  function missingContact(state) {
    if (!state || (state.type !== 'branch' && state.type !== 'courier')) return null;
    if (!splitName(state.name).last) return 'name';
    if (!normalizePhoneUA(state.phone)) return 'phone';
    return null;
  }

  function missingField(state) {
    if (!state || state.type === 'other') return null;
    if (!state.city) return 'city';
    if (state.type === 'branch') return state.warehouse ? null : 'warehouse';
    if (!state.street) return 'street';
    if (!clean(state.house)) return 'house';
    return null;
  }

  // Fields that hold a choice now, so a «choose from the list» warning left on
  // them is stale. The quick-city buttons fill the city without going through
  // the list, and the warning used to stay red under «Київ» (2026-10-05).
  function settledFields(state) {
    var done = [];
    if (!state || state.type === 'other') return done;
    if (state.city) done.push('city');
    if (state.warehouse) done.push('warehouse');
    if (state.street) done.push('street');
    return done;
  }

  function checkoutUrl(state, base) {
    base = base || '/checkout';
    if (!state || (state.type !== 'branch' && state.type !== 'courier') || missingField(state)) return base;
    var address1 = state.type === 'branch'
      ? clean(state.warehouse.description)
      : clean(state.street.name) + ', буд.' + clean(state.house) + (clean(state.flat) ? ', кв.' + clean(state.flat) : '') + ' (' + METHOD.courier + ')';
    var fields = { address1: address1, city: clean(state.city.name), zip: ZIP, country: COUNTRY };
    // Her name and phone travel with the address. Sending the address alone is
    // what makes Shopify open in red: it validates what it was given, finds no
    // name and no phone, and says so before she has touched anything.
    var name = splitName(state.name);
    if (name.first) fields.first_name = name.first;
    if (name.last) fields.last_name = name.last;
    var phone = normalizePhoneUA(state.phone);
    if (phone) fields.phone = phone;
    var parts = [];
    Object.keys(fields).forEach(function (k) {
      parts.push('checkout[shipping_address][' + k + ']=' + encodeURIComponent(fields[k]));
    });
    return base + '?' + parts.join('&');
  }

  // «Відділення №25 (до 30 кг на одне місце): вул. Левка Лук'яненка, 9»
  //  → { title: 'Відділення №25 (до 30 кг на одне місце)', address: "вул. Левка Лук'яненка, 9" }
  function splitDescription(description) {
    var d = clean(description);
    var i = d.indexOf(':');
    if (i === -1) return { title: d, address: '' };
    return { title: d.slice(0, i).trim(), address: d.slice(i + 1).trim() };
  }

  // Shopify's preview flag: ?hs_np_preview=1 turns it on for this browser,
  // =0 turns it off. Returns the new stored value, or null when the URL says nothing.
  function previewFlagFromSearch(search) {
    var m = /[?&]hs_np_preview=([01])(?:&|$)/.exec(String(search || ''));
    return m ? m[1] : null;
  }

  // ─── Browser widget ────────────────────────────────────────────────────────

  var CSS = [
    '.hs-np{--hs-np-ink:#663e19;--hs-np-line:rgba(102,62,25,.28);--hs-np-field:rgba(102,62,25,.6);--hs-np-soft:rgba(255,255,255,.62);--hs-np-err:#a3261b;',
    'color:var(--hs-np-ink);font:inherit;font-weight:400;margin:0 0 16px;text-align:left;letter-spacing:normal;text-transform:none}',
    '.hs-np *,.hs-np *::before,.hs-np *::after{box-sizing:border-box}',
    '.hs-np [hidden]{display:none!important}',
    '.hs-np__sr{position:absolute!important;width:1px!important;height:1px!important;margin:0!important;padding:0!important;overflow:hidden!important;clip:rect(0 0 0 0)!important;white-space:nowrap!important;border:0!important}',
    '.hs-np__title{font-size:19px;font-weight:700;line-height:1.25;margin:4px 0 10px;color:var(--hs-np-ink)}',
    '.hs-np__types{display:grid;gap:8px;margin:0 0 14px;padding:0;border:0;min-width:0}',
    '.hs-np__types legend{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}',
    '.hs-np__type{position:relative;display:flex;align-items:center;gap:10px;min-height:52px;margin:0;padding:10px 12px;border:1px solid var(--hs-np-line);border-radius:10px;background:var(--hs-np-soft);cursor:pointer;line-height:1.25}',
    '.hs-np__type input{position:absolute;opacity:0;width:1px;height:1px;margin:0;pointer-events:none}',
    '.hs-np__dot{flex:0 0 20px;height:20px;border-radius:50%;border:2px solid var(--hs-np-ink);background:#fff;display:grid;place-items:center}',
    '.hs-np__type input:checked+.hs-np__dot::after{content:"";width:10px;height:10px;border-radius:50%;background:var(--hs-np-ink)}',
    '.hs-np__type.is-checked{border:2px solid var(--hs-np-ink);padding:9px 11px;background:#fff}',
    '.hs-np__type input:focus-visible+.hs-np__dot{outline:2px solid var(--hs-np-ink);outline-offset:3px}',
    '.hs-np__type-text{display:flex;flex-direction:column;min-width:0}',
    '.hs-np__type-name{font-size:15px;font-weight:700}',
    '.hs-np__type-sub{font-size:13px;opacity:.8}',
    '.hs-np__field{margin:0 0 12px;min-width:0}',
    '.hs-np__label{display:block;font-size:14px;font-weight:700;margin:0 0 6px;color:var(--hs-np-ink);position:static;transform:none;opacity:1;pointer-events:auto}',
    '.hs-np__hint{font-weight:400;opacity:.8}',
    '.hs-np__hint-line{margin:6px 0 14px;font-size:13px;line-height:1.35;color:var(--hs-np-ink);opacity:.75}',
    '.hs-np__fee{margin:14px 0 0;font-size:13px;line-height:1.4;color:var(--hs-np-ink);opacity:.85}',
    // «До сплати»: the last thing she reads before the button. The figure is
    // the biggest number on the screen — it used to be the smallest.
    '.hs-money{margin:14px 0 12px;padding:14px 16px;border:1px solid var(--hs-np-line);border-radius:12px;background:#fff;color:var(--hs-np-ink);font-size:15px;line-height:1.4}',
    '.hs-money[hidden]{display:none}',
    '.hs-money__row{display:flex;align-items:baseline;justify-content:space-between;gap:12px;margin:0 0 6px}',
    '.hs-money__row[hidden]{display:none}',
    '.hs-money__k{opacity:.8}',
    '.hs-money__v{font-weight:700;font-variant-numeric:tabular-nums;white-space:nowrap}',
    '.hs-money__row--off .hs-money__v{color:#1f7a4d}',
    '.hs-money__row--total{margin:10px 0 0;padding:10px 0 0;border-top:1px solid var(--hs-np-line)}',
    '.hs-money__row--total .hs-money__k{font-size:12px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;opacity:.75}',
    '.hs-money__row--total .hs-money__v{font-size:22px;line-height:1.15}',
    '.hs-money-off{display:none!important}',
    // One checkout button, one colour. The same button was #F51111 on /cart
    // and #663E19 in the drawer — two identities depending on which door she
    // came through — and white on that red is 4.22:1, under the 4.5 AA needs
    // (critique 2026-09-21). The shop's own brown reads 9.26:1.
    '.cart__checkout-button,.cart__ctas .button--primary{background-color:#663E19!important;color:#fff!important;border-color:#663E19!important}',
    '.cart__checkout-button:hover,.cart__ctas .button--primary:hover{background-color:#52310f!important}',
    // An empty cart offered «Оформити Замовлення - ₴0.00 UAH», greyed out.
    '.hs-cart-empty .cart__ctas,.hs-cart-empty .hs-money{display:none!important}',
    // The field borders were rgba(102,62,25,.28) — 1.63:1, where a control
    // needs 3:1 to be seen as a control at all.
    '.hs-np__input{border-color:rgba(102,62,25,.6)}',
    '.hs-np__promises{list-style:none;margin:14px 0 0;padding:12px 0 0;border-top:1px solid var(--hs-np-line);display:grid;gap:7px}',
    '.hs-np__promises li{position:relative;padding-left:22px;font-size:13px;line-height:1.35;color:var(--hs-np-ink);opacity:.85}',
    // A drawn tick, not an emoji: the same mark in every browser, and it never
    // turns into a coloured picture next to the money.
    '.hs-np__promises li::before{content:"";position:absolute;left:4px;top:3px;width:5px;height:9px;border:solid currentColor;border-width:0 1.6px 1.6px 0;transform:rotate(42deg);opacity:.7}',
    '.hs-np__input{display:block;width:100%;min-height:46px;margin:0;padding:10px 12px;border:1px solid var(--hs-np-field);border-radius:8px;background:#fff;color:var(--hs-np-ink);font:inherit;font-size:16px;font-weight:400;line-height:1.3;box-shadow:none;-webkit-appearance:none;appearance:none}',
    '.hs-np__input::placeholder{color:rgba(102,62,25,.6);opacity:1}',
    '.hs-np__input:focus{outline:2px solid var(--hs-np-ink);outline-offset:1px;border-color:var(--hs-np-ink)}',
    '.hs-np__input[aria-invalid="true"]{border-color:var(--hs-np-err)}',
    '.hs-np__row{display:grid;grid-template-columns:1fr 1fr;gap:10px}',
    '.hs-np__chips{display:flex;flex-wrap:wrap;gap:6px;margin:8px 0 0;padding:0;list-style:none}',
    '.hs-np__chip{min-height:36px;margin:0;padding:6px 12px;border:1px solid var(--hs-np-line);border-radius:18px;background:var(--hs-np-soft);color:var(--hs-np-ink);font:inherit;font-size:14px;font-weight:700;cursor:pointer;box-shadow:none}',
    '.hs-np__chip:focus-visible{outline:2px solid var(--hs-np-ink);outline-offset:2px}',
    '.hs-np__list{margin:6px 0 0;padding:4px;list-style:none;max-height:300px;overflow-y:auto;overscroll-behavior:contain;background:#fff;border:1px solid var(--hs-np-line);border-radius:8px}',
    '.hs-np__list[hidden]{display:none}',
    '.hs-np__option{display:block;min-height:44px;padding:9px 10px;border-radius:6px;cursor:pointer;line-height:1.3;font-size:15px}',
    '.hs-np__option[aria-selected="true"],.hs-np__option:hover{background:rgba(102,62,25,.1)}',
    '.hs-np__option-main{display:block;font-weight:700}',
    '.hs-np__option-sub{display:block;font-size:13px;opacity:.85;margin-top:2px}',
    '.hs-np__note{display:block;padding:10px;font-size:14px;line-height:1.35}',
    '.hs-np__msg{font-size:13px;line-height:1.35;margin:6px 0 0}',
    '.hs-np__msg:empty{display:none}',
    '.hs-np__msg--err{color:var(--hs-np-err);font-weight:700}',
    '.hs-np__chosen{display:flex;align-items:flex-start;gap:10px;padding:10px 12px;border:2px solid var(--hs-np-ink);border-radius:8px;background:#fff}',
    '.hs-np__chosen-text{flex:1;min-width:0;line-height:1.3;overflow-wrap:anywhere}',
    '.hs-np__chosen-main{display:block;font-weight:700;font-size:15px}',
    '.hs-np__chosen-sub{display:block;font-size:13px;opacity:.85;margin-top:2px}',
    '.hs-np__change{flex:0 0 auto;min-height:36px;margin:-4px -4px 0 0;padding:6px 10px;border:0;background:none;color:var(--hs-np-ink);font:inherit;font-size:14px;font-weight:700;text-decoration:underline;cursor:pointer;box-shadow:none}',
    '.hs-np__change:focus-visible{outline:2px solid var(--hs-np-ink);outline-offset:2px}',
    '.hs-np-on #nova-poshta{display:none!important}',
    // Delivery right under the items (2026-09-17 critique): the drawer showed
    // ~550px of guarantee lists, the promo box and the note first. The order
    // summary (picker, total, checkout) now leads; the rest follows below.
    '.hs-np-on .dbtfy-cart-drawer__footer{display:flex;flex-direction:column}',
    '.hs-np-on .dbtfy-cart-drawer__footer>.cart-order-summary{order:-1}',
    // The «Оберіть спосіб доставки нижче ↓» pill pointed at the old spot.
    '.hs-np-on #hsCartCue{display:none!important}',
    '.hs-np-busy{opacity:.7;pointer-events:none}',
    '@media (prefers-reduced-motion:no-preference){.hs-np__type,.hs-np__chip,.hs-np__option{transition:background-color .15s,border-color .15s}}',
  ].join('\n');

  var QUICK_CITIES = [
    { name: 'Київ', label: 'м. Київ, Київська обл.', ref: '8d5a980d-391c-11dd-90d9-001a92567626' },
    { name: 'Львів', label: 'м. Львів, Львівська обл.', ref: 'db5c88f5-391c-11dd-90d9-001a92567626' },
    { name: 'Одеса', label: 'м. Одеса, Одеська обл.', ref: 'db5c88d0-391c-11dd-90d9-001a92567626' },
    { name: 'Харків', label: 'м. Харків, Харківська обл.', ref: 'db5c88e0-391c-11dd-90d9-001a92567626' },
    { name: 'Дніпро', label: 'м. Дніпро, Дніпропетровська обл.', ref: 'db5c88f0-391c-11dd-90d9-001a92567626' },
  ];

  function boot(win, doc) {
    var cfg = win.HSNP || {};
    if (!cfg.api || win.__HS_NP_BOOTED) return;
    win.__HS_NP_BOOTED = true;

    if (cfg.mode === 'preview') {
      var flag = previewFlagFromSearch(win.location.search);
      try {
        if (flag === '1') win.localStorage.setItem('hs_np_preview', '1');
        if (flag === '0') win.localStorage.removeItem('hs_np_preview');
        if (win.localStorage.getItem('hs_np_preview') !== '1') return;
      } catch (e) { if (flag !== '1') return; }
    }

    var api = String(cfg.api).replace(/\/+$/, '');
    if (win.location.protocol === 'https:') api = api.replace(/^http:\/\//, 'https://');   // a plain http call from an https page is blocked
    var mountSelector = cfg.mountSelector || '.dbtfy-cart-drawer__footer .cart-order-summary';
    var checkoutSelector = cfg.checkoutSelector || 'button[name="checkout"], input[name="checkout"], .cart__checkout-button, a[href="/checkout"]';

    var state = emptyState();
    var cartTotal = 0;           // ₴, for the cash-on-delivery fee line
    var cartSubtotal = 0;        // ₴, before any discount — the «Товари» line
    var cartDiscount = 0;        // ₴, what the cart already took off
    var cartToken = '';          // which cart this is, for the delivery-price halves
    var price = { key: null, text: null, asking: false };   // the delivery line, once it is known
    // «?np=1» always shows the delivery price, whichever half this cart is in —
    // for trying it on a phone. Such a cart is left out of the comparison.
    var priceTest = false;
    try { priceTest = /[?&]np=1\b/.test(win.location.search || ''); } catch (e) { priceTest = false; }
    var loaded = false;          // cart attributes read
    var lookupFailures = 0;      // after 2 failures a lookup outage stops blocking checkout
    var saveChain = Promise.resolve();
    var lastSaved = '';
    var uid = 0;
    var mounts = [];
    var cache = {};

    doc.documentElement.classList.add('hs-np-on');
    injectCss();

    // The Telegram confirmation popup (hs-tg.js) asks for the same number this
    // block does. It reads what she typed here so it opens filled in, and hands
    // back the number she confirmed — verified beats typed.
    win.HSNPLive = {
      getContact: function () { return { name: clean(state.name), phone: clean(state.phone) }; },
      setContact: function (c) {
        if (!c) return;
        var patch = {};
        if (c.name != null) patch.name = clean(c.name);
        if (c.phone != null) patch.phone = clean(c.phone);
        if (!('name' in patch) && !('phone' in patch)) return;
        setState(patch);
        rememberContact();
      },
    };

    // ── data ──
    function getJson(path) {
      if (cache[path]) return Promise.resolve(cache[path]);
      return fetch(api + path, { cache: 'default' }).then(function (r) {
        return r.json().catch(function () { return null; }).then(function (j) {
          if (!r.ok || !j || !j.ok) {
            var e = new Error((j && j.error) || '');
            e.status = r.status;
            throw e;
          }
          cache[path] = j;
          lookupFailures = 0;
          return j;
        });
      }).catch(function (err) {
        // Too many requests is the shopper typing fast, not an outage.
        if (err && err.status === 429) throw new Error(err.message || 'Зачекай хвилинку і спробуй ще раз.');
        lookupFailures += 1;
        var text = (err && err.status && err.message) || 'Не вдалося завантажити список. Перевір інтернет і спробуй ще раз.';
        if (lookupFailures >= 2) text += ' Або оформлюй замовлення, адресу вкажеш на наступному кроці.';
        throw new Error(text);
      });
    }

    function saveKey() {
      var body = { attributes: buildAttributes(state) };
      var noteEl = doc.querySelector('textarea[name="note"]');
      if (noteEl) body.note = noteEl.value;
      return JSON.stringify(body);
    }

    function saveAttributes() {
      var key = saveKey();
      if (key === lastSaved) return saveChain;
      lastSaved = key;
      saveChain = saveChain.then(function () {
        return fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: key,
        }).then(function (r) { if (!r.ok) throw new Error('cart ' + r.status); });
      }).catch(function (err) {
        lastSaved = '';
        if (win.console) win.console.warn('[hs-np] saving the delivery choice failed:', err && err.message);
      });
      return saveChain;
    }

    // A new cart starts with no attributes, so the cart itself cannot
    // remember her between orders — this browser can. Her own phone, her own
    // device; nothing leaves it until she checks out.
    function rememberContact() {
      try {
        win.localStorage.setItem('hs_np_contact', JSON.stringify({ name: clean(state.name), phone: clean(state.phone) }));
      } catch (e) { /* private mode: she types it once more, nothing breaks */ }
    }
    function rememberedContact() {
      try {
        var c = JSON.parse(win.localStorage.getItem('hs_np_contact') || 'null');
        return c && typeof c === 'object' ? { name: clean(c.name), phone: clean(c.phone) } : null;
      } catch (e) { return null; }
    }

    // International leaves no attribute behind (nothing of Nova Poshta's may
    // reach that order), so the cart cannot remember it; this browser does.
    function rememberType() {
      try { win.sessionStorage.setItem('hs_np_type', state.type); } catch (e) {}
    }
    function rememberedType() {
      try { return win.sessionStorage.getItem('hs_np_type'); } catch (e) { return null; }
    }

    function setState(patch, opts) {
      for (var k in patch) state[k] = patch[k];
      if ('type' in patch) rememberType();
      mounts.forEach(function (m) { sync(m, opts); });
      if (loaded) saveAttributes();
    }

    // ── DOM helpers ──
    function el(tag, attrs, children) {
      var node = doc.createElement(tag);
      if (attrs) for (var k in attrs) {
        if (attrs[k] === null || attrs[k] === undefined || attrs[k] === false) continue;
        if (k === 'text') node.textContent = attrs[k];
        else if (k === 'className') node.className = attrs[k];
        else node.setAttribute(k, attrs[k] === true ? '' : attrs[k]);
      }
      (children || []).forEach(function (c) { if (c) node.appendChild(typeof c === 'string' ? doc.createTextNode(c) : c); });
      return node;
    }

    function injectCss() {
      if (doc.getElementById('hs-np-css')) return;
      var s = doc.createElement('style');
      s.id = 'hs-np-css';
      s.textContent = CSS;
      (doc.head || doc.documentElement).appendChild(s);
    }

    // Smooth scroll needs animation frames, and a phone in power saving (or a
    // page the browser treats as hidden) may not give any: the button then
    // looked dead — the red line was set, but out of sight under the sticky
    // checkout bar. If the node is still off screen when the animation should
    // be over, jump there.
    function bringIntoView(node, block) {
      if (!node || !node.scrollIntoView) return;
      node.scrollIntoView({ behavior: 'smooth', block: block });
      setTimeout(function () {
        var r = node.getBoundingClientRect();
        var h = win.innerHeight || doc.documentElement.clientHeight;
        if (r.top < 0 || r.bottom > h * 0.8) node.scrollIntoView({ block: block });
      }, 600);
    }

    function debounce(fn, ms) {
      var t;
      return function () { var args = arguments, self = this; clearTimeout(t); t = setTimeout(function () { fn.apply(self, args); }, ms); };
    }

    // ── one combobox (city / warehouse / street) ──
    function combobox(m, name, opts) {
      var id = 'hs-np-' + name + '-' + m.id;
      var input = el('input', {
        id: id, className: 'hs-np__input', type: 'text', role: 'combobox', autocomplete: 'off', autocapitalize: 'off',
        spellcheck: 'false', enterkeyhint: 'search', placeholder: opts.placeholder,
        'aria-autocomplete': 'list', 'aria-expanded': 'false', 'aria-controls': id + '-list', 'aria-describedby': id + '-msg',
      });
      var list = el('ul', { id: id + '-list', className: 'hs-np__list', role: 'listbox', 'aria-label': opts.label, hidden: true });
      var msg = el('p', { id: id + '-msg', className: 'hs-np__msg', 'aria-live': 'polite' });
      var chosen = el('div', { className: 'hs-np__chosen', hidden: true });
      var field = el('div', { className: 'hs-np__field', 'data-field': name }, [
        el('label', { className: 'hs-np__label', for: id, text: opts.label }),
        chosen, input, list, msg, opts.after || null,
      ]);
      var cb = { field: field, input: input, list: list, msg: msg, chosen: chosen, items: [], active: -1, seq: 0 };

      function close() {
        list.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
        cb.active = -1;
      }
      function note(text) {
        list.textContent = '';
        list.appendChild(el('li', { className: 'hs-np__note', role: 'presentation', text: text }));
        list.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      }
      function render(items) {
        cb.items = items;
        cb.active = -1;
        list.textContent = '';
        items.forEach(function (item, i) {
          var li = el('li', { id: id + '-opt-' + i, className: 'hs-np__option', role: 'option', 'aria-selected': 'false' }, [
            el('span', { className: 'hs-np__option-main', text: item.main }),
            item.sub ? el('span', { className: 'hs-np__option-sub', text: item.sub }) : null,
          ]);
          li.addEventListener('mousedown', function (e) { e.preventDefault(); });   // keep focus in the input
          li.addEventListener('click', function () { pick(i); });
          list.appendChild(li);
        });
        list.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      }
      function highlight(i) {
        var opts_ = list.querySelectorAll('[role="option"]');
        if (!opts_.length) return;
        cb.active = (i + opts_.length) % opts_.length;
        for (var j = 0; j < opts_.length; j++) opts_[j].setAttribute('aria-selected', j === cb.active ? 'true' : 'false');
        input.setAttribute('aria-activedescendant', opts_[cb.active].id);
        if (opts_[cb.active].scrollIntoView) opts_[cb.active].scrollIntoView({ block: 'nearest' });
      }
      function pick(i) {
        var item = cb.items[i];
        if (!item) return;
        close();
        msg.textContent = '';
        msg.className = 'hs-np__msg';
        input.removeAttribute('aria-invalid');
        opts.onPick(item.value);
      }
      var search = debounce(function () {
        var q = input.value.trim();
        var seq = ++cb.seq;
        if (q.length < (opts.minChars || 0)) { if (opts.onShort) opts.onShort(q); else close(); return; }
        // On a slow phone connection an empty gap reads as "nothing found".
        var slow = setTimeout(function () { if (seq === cb.seq && doc.activeElement === input) note('Шукаю…'); }, 400);
        opts.fetch(q).then(function (items) {
          clearTimeout(slow);
          if (seq !== cb.seq) return;                     // a newer query is on its way
          if (!items.length) { note(opts.empty(q)); return; }
          render(items);
          if (items.length === 1 || opts.autoHighlight) highlight(0);
        }).catch(function (err) {
          clearTimeout(slow);
          if (seq !== cb.seq) return;
          note((err && err.message) || 'Не вдалося завантажити. Спробуй ще раз.');
        });
      }, 220);

      input.addEventListener('input', function () {
        input.removeAttribute('aria-invalid');
        if (msg.className.indexOf('--err') !== -1) { msg.textContent = ''; msg.className = 'hs-np__msg'; }
        search();
      });
      input.addEventListener('focus', function () { if (opts.openOnFocus) search(); });
      input.addEventListener('blur', function () {
        setTimeout(function () { if (doc.activeElement !== input) close(); }, 150);
      });
      input.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown') { e.preventDefault(); if (list.hidden) search(); else highlight(cb.active + 1); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); highlight(cb.active - 1); }
        else if (e.key === 'Enter') { if (!list.hidden && cb.active >= 0) { e.preventDefault(); pick(cb.active); } else if (!list.hidden) e.preventDefault(); }
        else if (e.key === 'Escape') { if (!list.hidden) { e.preventDefault(); close(); } }
      });

      cb.close = close;
      cb.showChosen = function (main, sub, changeLabel, onChange) {
        chosen.textContent = '';
        var btn = el('button', { type: 'button', className: 'hs-np__change', text: 'Змінити', 'aria-label': changeLabel });
        btn.addEventListener('click', onChange);
        chosen.appendChild(el('div', { className: 'hs-np__chosen-text' }, [
          el('span', { className: 'hs-np__chosen-main', text: main }),
          sub ? el('span', { className: 'hs-np__chosen-sub', text: sub }) : null,
        ]));
        chosen.appendChild(btn);
        chosen.hidden = false;
        input.hidden = true;
        close();
      };
      cb.showInput = function (focus) {
        chosen.hidden = true;
        input.hidden = false;
        if (focus) input.focus();
      };
      cb.error = function (text) {
        msg.textContent = text;
        msg.className = 'hs-np__msg hs-np__msg--err';
        input.setAttribute('aria-invalid', 'true');
      };
      cb.clearError = function () {
        if (msg.className.indexOf('--err') === -1) return;
        msg.textContent = '';
        msg.className = 'hs-np__msg';
        input.removeAttribute('aria-invalid');
      };
      return cb;
    }

    // ── one widget instance ──
    function build(host) {
      var m = { id: ++uid, host: host };
      // Town, branch and phone stay out of Clarity recordings.
      var root = el('div', { className: 'hs-np', 'data-hs-np': VERSION, 'data-clarity-mask': 'true' });
      m.root = root;
      root.addEventListener('focusin', function (e) { m.focused = e.target; });
      root.addEventListener('focusout', function (e) {
        // Leaving for somewhere else on the page forgets it; a removal (no
        // relatedTarget) keeps it, so a re-render can put focus back.
        if (e.relatedTarget && !root.contains(e.relatedTarget)) { m.focused = null; return; }
        // A tap on empty page also has no relatedTarget; tell it from a removal
        // by whether the widget is still on the page a moment later.
        var left = e.target;
        setTimeout(function () { if (root.isConnected && m.focused === left && doc.activeElement !== left) m.focused = null; }, 0);
      });

      // Name and phone first: she is already here, and typing them now means
      // the checkout opens filled in instead of opening in red.
      root.appendChild(el('h3', { className: 'hs-np__title', text: 'Отримувачка' }));
      m.name = el('input', { id: 'hs-np-name-' + m.id, className: 'hs-np__input', type: 'text', autocomplete: 'name', autocapitalize: 'words', maxlength: '60', 'aria-describedby': 'hs-np-name-msg-' + m.id });
      m.nameMsg = el('p', { id: 'hs-np-name-msg-' + m.id, className: 'hs-np__msg', 'aria-live': 'polite' });
      m.phone = el('input', { id: 'hs-np-phone-' + m.id, className: 'hs-np__input', type: 'tel', autocomplete: 'tel', inputmode: 'tel', maxlength: '20', 'aria-describedby': 'hs-np-phone-msg-' + m.id });
      m.phoneMsg = el('p', { id: 'hs-np-phone-msg-' + m.id, className: 'hs-np__msg', 'aria-live': 'polite' });
      var persistContact = debounce(function () {
        setState({ name: m.name.value, phone: m.phone.value }, { typing: true });
        rememberContact();
      }, 400);
      m.name.addEventListener('input', function () {
        m.name.removeAttribute('aria-invalid');
        m.nameMsg.textContent = '';
        persistContact();
      });
      m.phone.addEventListener('input', function () {
        m.phone.removeAttribute('aria-invalid');
        m.phoneMsg.textContent = '';
        persistContact();
      });
      // Only after she leaves the field — a hint while she is still typing the
      // third digit is noise, not help.
      m.phone.addEventListener('blur', function () {
        var v = clean(m.phone.value);
        if (!v || normalizePhoneUA(v)) return;
        m.phone.setAttribute('aria-invalid', 'true');
        m.phoneMsg.textContent = 'Вкажи номер у форматі 067 123 45 67: курʼєр Нової пошти дзвонить на нього.';
        m.phoneMsg.className = 'hs-np__msg hs-np__msg--err';
      });
      root.appendChild(el('div', { className: 'hs-np__row' }, [
        el('div', { className: 'hs-np__field' }, [el('label', { className: 'hs-np__label', for: m.name.id, text: 'Імʼя та прізвище' }), m.name, m.nameMsg]),
        el('div', { className: 'hs-np__field' }, [el('label', { className: 'hs-np__label', for: m.phone.id, text: 'Телефон' }), m.phone, m.phoneMsg]),
      ]));
      root.appendChild(el('p', { className: 'hs-np__hint-line', text: 'Підставимо їх у Telegram і в оформлення замовлення, вдруге вводити не треба.' }));

      root.appendChild(el('h3', { className: 'hs-np__title', id: 'hs-np-title-' + m.id, text: 'Доставка' }));

      var types = el('fieldset', { className: 'hs-np__types' }, [el('legend', { text: 'Спосіб доставки' })]);
      m.radios = {};
      [
        { v: 'branch', name: 'Відділення або поштомат', sub: 'Нова пошта' },
        { v: 'courier', name: 'Курʼєр до дверей', sub: 'Нова пошта' },
        { v: 'other', name: 'Міжнародна доставка', sub: 'Адресу вкажеш на наступному кроці' },
      ].forEach(function (t) {
        var radio = el('input', { type: 'radio', name: 'hs-np-type-' + m.id, value: t.v });
        radio.addEventListener('change', function () {
          if (!radio.checked) return;
          var patch = { type: t.v, warehouse: null, street: null, house: '', flat: '' };
          m.alert.textContent = '';
          setState(patch);
        });
        m.radios[t.v] = radio;
        types.appendChild(el('label', { className: 'hs-np__type' }, [
          radio, el('span', { className: 'hs-np__dot', 'aria-hidden': 'true' }),
          el('span', { className: 'hs-np__type-text' }, [
            el('span', { className: 'hs-np__type-name', text: t.name }),
            el('span', { className: 'hs-np__type-sub', text: t.sub }),
          ]),
        ]));
      });
      root.appendChild(types);

      // City
      var chips = el('ul', { className: 'hs-np__chips', 'aria-label': 'Популярні міста' });
      QUICK_CITIES.forEach(function (c) {
        var b = el('button', { type: 'button', className: 'hs-np__chip', text: c.name });
        b.addEventListener('click', function () { chooseCity(c, true); });
        chips.appendChild(el('li', null, [b]));
      });
      m.chips = chips;
      m.city = combobox(m, 'city', {
        label: 'Місто або село',
        placeholder: 'Почни вводити назву',
        minChars: 2,
        after: chips,
        fetch: function (q) {
          return getJson('/cities?q=' + encodeURIComponent(q)).then(function (j) {
            return (j.cities || [])
              .filter(function (c) { return state.type === 'courier' ? c.courier : c.warehouses > 0; })
              .map(function (c) { return { main: c.name, sub: c.label, value: c }; });
          });
        },
        empty: function () {
          return state.type === 'courier'
            ? 'Не знайшли, куди везе курʼєр. Перевір назву або обери відділення.'
            : 'Не знайшли такого міста з відділенням Нової пошти. Перевір назву.';
        },
        onShort: function () { m.city.close(); },
        onPick: function (c) { chooseCity(c, true); },
      });
      root.appendChild(m.city.field);

      function chooseCity(c, moveOn) {
        setState({ city: { ref: c.ref, name: c.name, label: c.label || c.name }, warehouse: null, street: null, house: '', flat: '' });
        // Ask for the branch list now, not when she taps the field: the answer
        // lands in the same cache the field reads, so the list is already there
        // (measured 1.2–2.5 s on a phone when it starts at the tap).
        if (c.ref && state.type === 'branch') {
          getJson('/warehouses?city=' + encodeURIComponent(c.ref) + '&q=').catch(function () { /* the field will ask again */ });
        }
        if (moveOn) {
          var next = state.type === 'courier' ? m.street : m.warehouse;
          // In the drawer the list opens under the field and the sticky
          // checkout bar covered all but the first branch; bring the field up.
          if (next) setTimeout(function () {
            next.input.focus({ preventScroll: true });
            bringIntoView(next.field, 'start');
          }, 0);
        }
      }

      // Warehouse
      m.warehouse = combobox(m, 'warehouse', {
        label: 'Відділення або поштомат',
        placeholder: 'Номер або вулиця',
        minChars: 0,
        openOnFocus: true,
        fetch: function (q) {
          if (!state.city) return Promise.resolve([]);
          return getJson('/warehouses?city=' + encodeURIComponent(state.city.ref) + '&q=' + encodeURIComponent(q)).then(function (j) {
            return (j.warehouses || []).map(function (w) {
              var parts = splitDescription(w.description);
              var sub = parts.address || w.shortAddress;
              if (w.restricted) sub = sub + ' · лише для мешканців будинку';
              return { main: parts.title, sub: sub, value: w };
            });
          });
        },
        empty: function (q) {
          var where = state.city ? ' у ' + state.city.name : '';
          return q ? 'Нічого не знайшли' + where + '. Спробуй номер або назву вулиці.' : 'Немає відділень' + where + '.';
        },
        onPick: function (w) { setState({ warehouse: w }); m.alert.textContent = ''; },
      });
      root.appendChild(m.warehouse.field);

      // Courier address
      m.street = combobox(m, 'street', {
        label: 'Вулиця',
        placeholder: 'Почни вводити назву',
        minChars: 2,
        fetch: function (q) {
          if (!state.city) return Promise.resolve([]);
          return getJson('/streets?city=' + encodeURIComponent(state.city.ref) + '&q=' + encodeURIComponent(q)).then(function (j) {
            return (j.streets || []).map(function (s) { return { main: s.name.replace(/^(\S+\.)(?=\S)/, '$1 '), value: s }; });
          });
        },
        empty: function () { return 'Не знайшли таку вулицю. Спробуй без «вул.» чи «просп.».'; },
        onPick: function (s) {
          setState({ street: s });
          setTimeout(function () { m.house.focus(); }, 0);
        },
      });
      m.house = el('input', { id: 'hs-np-house-' + m.id, className: 'hs-np__input', type: 'text', autocomplete: 'off', maxlength: '20', 'aria-describedby': 'hs-np-house-msg-' + m.id });
      m.flat = el('input', { id: 'hs-np-flat-' + m.id, className: 'hs-np__input', type: 'text', autocomplete: 'off', maxlength: '20' });
      m.houseMsg = el('p', { id: 'hs-np-house-msg-' + m.id, className: 'hs-np__msg', 'aria-live': 'polite' });
      var persistHouse = debounce(function () { setState({ house: m.house.value, flat: m.flat.value }, { typing: true }); }, 400);
      m.house.addEventListener('input', function () { m.house.removeAttribute('aria-invalid'); m.houseMsg.textContent = ''; persistHouse(); });
      m.flat.addEventListener('input', persistHouse);
      m.courier = el('div', { className: 'hs-np__courier' }, [
        m.street.field,
        el('div', { className: 'hs-np__row' }, [
          el('div', { className: 'hs-np__field' }, [el('label', { className: 'hs-np__label', for: m.house.id, text: 'Будинок' }), m.house, m.houseMsg]),
          el('div', { className: 'hs-np__field' }, [el('label', { className: 'hs-np__label', for: m.flat.id }, ['Квартира ', el('span', { className: 'hs-np__hint', text: '(якщо є)' })]), m.flat]),
        ]),
      ]);
      root.appendChild(m.courier);

      // What the shop promises, on the last screen before she chooses how to
      // pay. Owner, 21.09.2026: an order paid online is worth 937 ₴ more than a
      // cash-on-delivery one, and a third of COD parcels are never collected —
      // and what makes people choose COD here is fear, not money. These three
      // promises shout on every product page and then disappear. Shopify only
      // lets an app put them inside the checkout on Plus (this shop is on
      // Basic), so they go where the shop's own code still renders: here.
      // Wording is the storefront's own; nothing new is promised.
      // The cash-on-delivery commission and «доставка за наш рахунок» used to
      // be printed here, beside the delivery choice. They now belong to the
      // «До сплати» card above the button, where the money is decided — the
      // same sentences, one place (critique 2026-09-21).

      var promises = el('ul', { className: 'hs-np__promises' });
      [
        '1 рік гарантії: порветься чи розтягнеться, замінимо на нове за 0 грн',
        'Безкоштовний обмін розміру, якщо не підійде',
        'Не побачиш ефекту? Повернемо гроші',
      ].forEach(function (line) {
        promises.appendChild(el('li', { text: line }));
      });
      root.appendChild(promises);

      m.alert = el('p', { className: 'hs-np__sr', role: 'alert' });
      root.appendChild(m.alert);

      host.insertBefore(root, host.firstChild);
      sync(m);
      return m;
    }

    function sync(m, opts) {
      var t = state.type;
      m.radios[t].checked = true;
      for (var r in m.radios) m.radios[r].parentNode.classList.toggle('is-checked', r === t);   // :has() is missing on older iPhones
      var np = t === 'branch' || t === 'courier';
      m.city.field.hidden = !np;
      m.warehouse.field.hidden = t !== 'branch' || !state.city;
      m.courier.hidden = t !== 'courier' || !state.city;

      if (state.city) {
        m.city.showChosen(state.city.name, state.city.label !== state.city.name ? state.city.label : '', 'Змінити місто', function () {
          setState({ city: null, warehouse: null, street: null, house: '', flat: '' });
          m.city.input.value = '';
          m.city.showInput(true);
        });
        m.chips.hidden = true;
      } else {
        m.city.showInput(false);
        m.chips.hidden = false;
      }

      if (state.warehouse) {
        var parts = splitDescription(state.warehouse.description);
        m.warehouse.showChosen(parts.title, parts.address || state.warehouse.shortAddress, 'Змінити відділення', function () {
          setState({ warehouse: null });
          m.warehouse.input.value = '';
          m.warehouse.showInput(true);
        });
      } else {
        m.warehouse.showInput(false);
      }

      if (state.street) {
        m.street.showChosen(state.street.name.replace(/^(\S+\.)(?=\S)/, '$1 '), '', 'Змінити вулицю', function () {
          setState({ street: null });
          m.street.input.value = '';
          m.street.showInput(true);
        });
      } else {
        m.street.showInput(false);
      }
      settledFields(state).forEach(function (f) { m[f].clearError(); });
      if (!missingField(state)) m.alert.textContent = '';

      // Both money sentences live in the «До сплати» card now — directly above
      // the button, each fact stated once (critique 2026-09-21).
      syncMoney(m);
      askPrice();
      if (!(opts && opts.typing)) {
        if (doc.activeElement !== m.house) m.house.value = state.house || '';
        if (doc.activeElement !== m.flat) m.flat.value = state.flat || '';
        if (doc.activeElement !== m.name) m.name.value = state.name || '';
        if (doc.activeElement !== m.phone) m.phone.value = state.phone || '';
      }
    }

    // ── «До сплати» ─────────────────────────────────────────────────────────
    // The theme prints the amount she owes at 11px between two marketing
    // blocks, with no breakdown at all — the only legible total is the one
    // baked into the button (critique 2026-09-21). This card is the last thing
    // she reads before pressing it: what the goods cost, that delivery is on
    // us, what she pays, and what cash on delivery adds at the branch. The two
    // lines it carries used to sit in the picker; they are stated once, here,
    // where the money decision is made.
    function money(n) {
      // Same as the theme (snippets/hs-uah.liquid) and the checkout:
      // «2 794 ₴», «2 793,90 ₴» — kopiykas only when there are some.
      var whole = Math.round(n * 100) % 100 === 0;
      var t = whole ? Math.round(n).toLocaleString('uk-UA') : n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return t.replace(/\s/g, '\u00a0') + '\u00a0₴';
    }

    function moneyRow(card, label, cls) {
      var row = el('div', { className: 'hs-money__row' + (cls ? ' ' + cls : '') });
      row.appendChild(el('span', { className: 'hs-money__k', text: label }));
      var v = el('span', { className: 'hs-money__v' });
      row.appendChild(v);
      card.appendChild(row);
      return { row: row, value: v };
    }

    // The child of `box` that contains `node` — the theme wraps each part of
    // the footer in its own `shopify-block`, and that wrapper is what we place
    // the card against.
    function blockOf(box, node) {
      for (var n = node; n && n !== box; n = n.parentNode) if (n.parentNode === box) return n;
      return null;
    }

    // The card belongs to the cart footer, not to the picker: it sits directly
    // above the checkout button, in both the cart page and the drawer.
    function moneyHost(m) {
      var box = m.host.closest ? m.host.closest('cart-order-summary') : null;
      if (!box) box = m.host.closest ? m.host.closest('form') : null;
      return box || null;
    }

    function buildMoney(m) {
      var box = moneyHost(m);
      if (!box || box.querySelector('.hs-money')) return null;
      var card = el('div', { className: 'hs-money' });
      var c = { card: card };
      c.goods = moneyRow(card, 'Товари');
      c.discount = moneyRow(card, 'Знижка', 'hs-money__row--off');
      c.ship = moneyRow(card, 'Доставка');
      c.total = moneyRow(card, 'До сплати', 'hs-money__row--total');
      // Before the block that holds the checkout button; at the end of the
      // footer when the theme has not rendered that block yet.
      var anchor = blockOf(box, box.querySelector('[name="checkout"]'));
      if (anchor) box.insertBefore(card, anchor); else box.appendChild(card);
      // One total on the screen: the theme's 11px line says the same thing.
      var themeTotal = blockOf(box, box.querySelector('[id*="cart_subtotal"], .totals'));
      if (themeTotal) themeTotal.classList.add('hs-money-off');
      return c;
    }

    function syncMoney(m) {
      if (!m.money || !m.money.card.isConnected) m.money = buildMoney(m);
      var c = m.money;
      if (!c) return;
      var box = c.card.parentElement;
      if (box) box.classList.toggle('hs-cart-empty', !(cartTotal > 0));
      if (!(cartTotal > 0)) { c.card.hidden = true; return; }
      c.card.hidden = false;
      c.goods.value.textContent = money(cartSubtotal || cartTotal);
      var off = cartDiscount > 0;
      c.discount.row.hidden = !off;
      if (off) c.discount.value.textContent = '−' + money(cartDiscount);
      // Delivery is the shop's cost, never hers.
      c.ship.value.textContent = '0 ₴ · платимо ми';
      c.total.value.textContent = money(cartTotal);
    }

    // ── mounting (Debutify re-renders the cart footer on every cart change) ──
    // A widget whose host was replaced is moved into the new host as it is —
    // same nodes, so a half-typed query, an open list and focus all survive.
    function mountAll() {
      var hosts = doc.querySelectorAll(mountSelector);
      var detached = [];
      mounts = mounts.filter(function (m) {
        var attached = doc.documentElement.contains(m.root);
        if (!attached) detached.push(m);
        return attached;
      });
      for (var i = 0; i < hosts.length; i++) {
        var host = hosts[i];
        if (host.querySelector('.hs-np')) continue;
        var m = detached.shift();
        if (!m) { mounts.push(build(host)); continue; }
        host.insertBefore(m.root, host.firstChild);
        m.host = host;
        mounts.push(m);
        sync(m);
        var nowFocused = doc.activeElement;
        if (m.focused && m.focused.isConnected && !m.focused.hidden && (!nowFocused || nowFocused === doc.body)) {
          m.focused.focus({ preventScroll: true });
        }
      }
    }
    // The three numbers the «До сплати» card needs. «Товари» is the price
    // before any discount — Shopify's items_subtotal_price is already after
    // product discounts like Buy20, which made the card read «Товари 3 985 ·
    // Знижка −996 · До сплати 3 985» (found 2026-09-27). Returns whether
    // anything changed, so a refresh only redraws when it has to.
    function readTotals(cart) {
      var total = cart && cart.total_price ? cart.total_price / 100 : 0;
      var off = cart && cart.total_discount ? cart.total_discount / 100 : 0;
      var goods = cart && cart.original_total_price ? cart.original_total_price / 100 : total + off;
      if (total === cartTotal && goods === cartSubtotal && off === cartDiscount) return false;
      cartTotal = total; cartSubtotal = goods; cartDiscount = off;
      return true;
    }

    function refreshTotal() {
      return fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          cartToken = (cart && cart.token) || cartToken;
          if (!readTotals(cart)) return;
          mounts.forEach(function (m) { sync(m); });
        })
        .catch(function () { /* the line simply stays as it was */ });
    }

    // ── «Доставка ≈104 ₴ — за наш рахунок» ──────────────────────────────────
    // The shop pays Nova Poshta, so she sees free delivery and never learns
    // what it is worth. Half the carts are told (the PO system decides which,
    // so both halves are counted the same way). It is never a charge, it never
    // blocks anything, and a cart that has not chosen a city is not asked.
    function priceKey() {
      if (!cartToken || !state.city || !state.city.ref) return null;
      if (state.type !== 'branch' && state.type !== 'courier') return null;
      if (state.type === 'branch' && !state.warehouse) return null;
      if (!(cartTotal > 0)) return null;
      return [state.city.ref, state.type, Math.round(cartTotal / 500)].join('|');
    }
    function askPrice() {
      var key = priceKey();
      if (!key || key === price.key || price.asking) return;
      price.asking = true;
      var q = '?cart=' + encodeURIComponent(cartToken)
        + '&city=' + encodeURIComponent(state.city.ref)
        + '&type=' + encodeURIComponent(state.type)
        + '&value=' + encodeURIComponent(Math.round(cartTotal))
        + (priceTest ? '&test=1' : '');
      // `api` points at the lookup base (…/api/np); this route lives at the
      // app root, so asking for api + '/api/delivery-price' hit
      // /api/np/api/delivery-price — a 404 whose CORS-less answer failed in the
      // console on every cart, and the line never showed (found 2026-09-21).
      fetch(api.replace(/\/api\/np\/?$/, '') + '/api/delivery-price' + q, { cache: 'no-store' })
        .then(function (r) { return r.json(); })
        .then(function (j) {
          price.key = key;
          price.text = j && j.ok && j.show && j.delivery
            ? 'Доставка Новою поштою приблизно ' + j.delivery + ' ₴. Платимо ми, з тебе 0 ₴.'
            : null;
          if (j && j.ok && j.arm) {
            // The order carries its half, so the two can be compared later.
            state.priceArm = j.arm;
            state.priceCart = String(cartToken).replace(/[^A-Za-z0-9]/g, '').slice(0, 32);
            saveAttributes();
          }
          mounts.forEach(function (m) { sync(m); });
        })
        .catch(function () { /* no line, no noise */ })
        .then(function () { price.asking = false; });
    }

    // The checkout buttons' words, in the cart page and the drawer, every time
    // the theme redraws them. Only text nodes and values are touched.
    var relabelButtons = function () {
      var btns = doc.querySelectorAll(checkoutSelector);
      for (var i = 0; i < btns.length; i++) {
        var b = btns[i];
        if (b.tagName === 'INPUT') { if (CHECKOUT_WORDS.test(b.value)) b.value = relabel(b.value); continue; }
        var walker = doc.createTreeWalker(b, 4, null);
        for (var t = walker.nextNode(); t; t = walker.nextNode()) {
          if (CHECKOUT_WORDS.test(t.nodeValue)) t.nodeValue = relabel(t.nodeValue);
        }
        var aria = b.getAttribute('aria-label');
        if (aria && CHECKOUT_WORDS.test(aria)) b.setAttribute('aria-label', relabel(aria));
      }
    };
    relabelButtons();
    var remount = debounce(function () { mountAll(); refreshTotal(); relabelButtons(); }, 60);
    // Only the theme's cart markup coming or going matters. Text changes and
    // our own nodes (buy bar, money card, size prompt, growth lab blocks…) used
    // to count too: on product pages the buy bar fed this a mutation every 60 ms
    // and each one fetched /cart.js (~13 a second, found 2026-10-06; live until
    // 14.10.2026).
    var cartSelector = mountSelector + ', cart-drawer, cart-drawer-items, cart-items, cart-notification, #main-cart-footer, .cart-order-summary';
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        if (cartMutation(records[i], cartSelector)) { remount(); return; }
      }
    }).observe(doc.body, { childList: true, subtree: true });

    // Restore the saved choice before drawing anything.
    fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var attrs = (cart && cart.attributes) || {};
        cartToken = (cart && cart.token) || '';
        cartTotal = cart && cart.total_price ? cart.total_price / 100 : 0;   // Shopify sends kopiykas
        readTotals(cart);
        state = stateFromAttributes(attrs);
        if (!state.name && !state.phone) {
          var known = rememberedContact();
          if (known) { state.name = known.name; state.phone = known.phone; }
        }
        if (!attrs._delivery_type && rememberedType() === 'other') state.type = 'other';
        return cart;
      })
      .then(function (cart) { return cart; }, function () { return null; })
      .then(function (cart) {
        loaded = true;
        var write = needsInitialWrite(cart);
        // What the cart already holds counts as saved: without this every page
        // view POSTed /cart/update.js (~0.7 s) and created a cart for visitors
        // who never added anything.
        if (!write) lastSaved = saveKey();
        mountAll();
        // The theme re-renders the cart footer as the page settles, so a mount
        // can already exist by the time the cart answers — and mountAll() only
        // inserts, it does not redraw. Without this the cash-on-delivery fee
        // line stayed hidden on a cold load: it was drawn while the total was
        // still zero and nothing asked it again.
        mounts.forEach(function (m) { sync(m); });
        if (write) saveAttributes();   // writes _delivery_type for a fresh cart with items, like the vendor widget did
      });

    // ── checkout ──
    function visibleMount() {
      var style = win.getComputedStyle ? function (n) { return win.getComputedStyle(n); } : null;
      for (var i = 0; i < mounts.length; i++) if (isShown(mounts[i].root, style)) return mounts[i];
      return mounts[0];
    }

    function blockCheckout(missing) {
      var m = visibleMount();
      if (!m) return;
      var text = {
        city: 'Обери місто зі списку, щоб оформити замовлення.',
        warehouse: 'Обери відділення або поштомат зі списку.',
        street: 'Обери вулицю зі списку.',
        house: 'Вкажи номер будинку.',
      }[missing];
      // The message goes under the field that is missing; the alert region only
      // repeats it for screen readers, visually hidden, so it is read out once.
      m.alert.textContent = text;
      var target = missing === 'city' ? m.city : missing === 'warehouse' ? m.warehouse : missing === 'street' ? m.street : null;
      if (target) { target.error(text); target.showInput(false); }
      if (missing === 'house') { m.house.setAttribute('aria-invalid', 'true'); m.houseMsg.textContent = text; m.houseMsg.className = 'hs-np__msg hs-np__msg--err'; }
      // The field itself, not the top of the form: from the drawer's button the
      // form top is «Отримувачка», and the red line could stay under the fold.
      bringIntoView(missing === 'house' ? m.house : target ? target.field : m.root, 'center');
      setTimeout(function () {
        var f = missing === 'house' ? m.house : target && target.input;
        if (f && !f.hidden) f.focus({ preventScroll: true });
      }, 350);
    }

    function blockContact(missing) {
      var m = visibleMount();
      if (!m) return;
      var field = missing === 'name' ? m.name : m.phone;
      var msg = missing === 'name' ? m.nameMsg : m.phoneMsg;
      var text = missing === 'name'
        ? 'Вкажи імʼя та прізвище так, як у паспорті: Нова пошта видає посилку за ним.'
        : 'Вкажи номер у форматі 067 123 45 67: на нього прийде SMS про посилку.';
      field.setAttribute('aria-invalid', 'true');
      msg.textContent = text;
      msg.className = 'hs-np__msg hs-np__msg--err';
      m.alert.textContent = text;
      bringIntoView(field, 'center');
      setTimeout(function () { field.focus({ preventScroll: true }); }, 350);
    }

    function onCheckout(e) {
      var btn = e.target && e.target.closest && e.target.closest(checkoutSelector);
      if (!btn || !loaded) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      if (btn.classList.contains('hs-np-busy')) return;

      // House and flat are saved after a pause in typing; take them as typed now.
      var vm = visibleMount();
      if (vm && state.type === 'courier' && state.street) {
        state.house = vm.house.value;
        state.flat = vm.flat.value;
      }

      // Name and phone are taken as typed now too (they save after a pause).
      if (vm && vm.name && vm.phone) { state.name = vm.name.value; state.phone = vm.phone.value; }

      var missing = missingField(state);
      // In form order: «Отримувачка» first, then the delivery. During a lookup
      // outage the address is not sent, so Shopify asks for everything itself.
      var contact = missingContact(state);
      if (contact && (!missing || lookupFailures < 2)) { blockContact(contact); return; }
      if (missing && lookupFailures < 2) { blockCheckout(missing); return; }

      btn.classList.add('hs-np-busy');
      btn.setAttribute('aria-busy', 'true');
      saveAttributes().then(function () {
        var url = checkoutUrl(state);
        // The Telegram confirmation popup (hs-tg.js), when the theme has it:
        // it decides whether to ask, then goes to the checkout itself. Without
        // that file — and if it ever throws — the cart behaves exactly as before.
        if (win.HSTG && typeof win.HSTG.before === 'function') {
          try { win.HSTG.before(url, btn); return; } catch (e) { /* fall through to the checkout */ }
        }
        win.location.href = url;
      });
      setTimeout(function () { btn.classList.remove('hs-np-busy'); btn.removeAttribute('aria-busy'); }, 8000);
    }
    doc.addEventListener('click', onCheckout, true);
    // Enter in a cart field submits the form with the checkout button as submitter
    // without a click on it.
    doc.addEventListener('submit', function (e) {
      var sub = e.submitter;
      if (sub && sub.name === 'checkout') onCheckout({ target: sub, preventDefault: function () { e.preventDefault(); }, stopPropagation: function () { e.stopPropagation(); } });
    }, true);

    // In preview the vendor widget is still on the page and disables the
    // checkout button until ITS fields are filled; a disabled button fires no
    // click, so keep ours clickable.
    if (cfg.mode === 'preview') {
      var unlock = function () {
        var btns = doc.querySelectorAll(checkoutSelector);
        for (var i = 0; i < btns.length; i++) {
          // Touch only what is set: a write of the same value still queues a
          // mutation record, and this observer would feed itself forever.
          if (btns[i].disabled) btns[i].disabled = false;
          if (btns[i].classList.contains('disabled')) btns[i].classList.remove('disabled');
        }
      };
      new MutationObserver(unlock).observe(doc.body, { attributes: true, attributeFilter: ['disabled', 'class'], subtree: true });
      unlock();
    }

    win.HSNP.debug = function () { return { version: VERSION, state: state, mounts: mounts.length, lookupFailures: lookupFailures }; };
  }

  return {
    VERSION: VERSION,
    ATTR_KEYS: ATTR_KEYS,
    buildAttributes: buildAttributes,
    stateFromAttributes: stateFromAttributes,
    missingField: missingField,
    missingContact: missingContact,
    settledFields: settledFields,
    isShown: isShown,
    cartMutation: cartMutation,
    needsInitialWrite: needsInitialWrite,
    checkoutUrl: checkoutUrl,
    splitDescription: splitDescription,
    splitName: splitName,
    codFee: codFee,
    relabel: relabel,
    CHECKOUT_LABEL: CHECKOUT_LABEL,
    COD_FEE: COD_FEE,
    normalizePhoneUA: normalizePhoneUA,
    previewFlagFromSearch: previewFlagFromSearch,
    emptyState: emptyState,
    boot: boot,
  };
});
